/**
 * @file Upload progress widget.
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Button from '../global/Button';

const propTypes = {
  total: PropTypes.number.isRequired,
  current: PropTypes.number.isRequired,
  success: PropTypes.bool,
  error: PropTypes.string,
  className: PropTypes.string,
  onDismiss: PropTypes.func,
  visible: PropTypes.bool,
  onSuccessButtonClick: PropTypes.func,
  successButtonText: PropTypes.string,
};

const UploadProgress = ({
  total = 0,
  current = 0,
  success,
  error,
  className = '',
  onDismiss,
  visible = false,
  onSuccessButtonClick,
  successButtonText = 'Done',
  renderError,
}) => {
  let width;

  if (success) {
    width = '100';
  } else if (current === 1) {
    // Show some initial progress to the user so that they
    // know that something is going on
    width = (1 / total / 2) * 100;
  } else {
    width = Math.max(((current - 1) / total) * 100, 0);
  }

  const classes = classNames({
    'upload-progress': true,
    [className]: className,
    'upload-progress-visible': visible,
  });

  const progressBarClasses = classNames({
    'progress-bar': true,
    'progress-bar-animating': !success,
  });

  return (
    <div className={classes}>
      {error && (
        <>
          {renderError ? (
            renderError()
          ) : (
            <p className="text-danger text-sm">
              I&rsquo;m sorry, an error occurred, please start the upload over.
              And don&rsquo;t worry, any duplicates will be eliminated.
            </p>
          )}
        </>
      )}

      {onDismiss && (success || error) && (
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          className="btn-dismiss btn-uploader-progress-dismiss"
        />
      )}

      <span className="uploader-progress-title text-sm">
        {success ? (
          <span>All photos uploaded successfully</span>
        ) : (
          <span>
            Uploading {current} of {total}
            ...
          </span>
        )}
      </span>

      <div className="progress">
        <div className={progressBarClasses} style={{ width: `${width}%` }} />
      </div>

      {onSuccessButtonClick && success && (
        <div className="mt-2 text-center">
          <Button btnSize="sm" onClick={onSuccessButtonClick}>
            {successButtonText}
          </Button>
        </div>
      )}
    </div>
  );
};

UploadProgress.propTypes = propTypes;

export default UploadProgress;
